import { DefaultError, useInfiniteQuery } from '@tanstack/react-query';
import { useInfiniteHandler } from 'hooks';
import { IInfinitePageParams, IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IContractProduct, IContractTrack, IContractVideo } from './types';

export const useGetContractProducts = (
  contractId: string,
  options?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    options?.page,
    options?.itemCount
  );

  return useInfiniteQuery<IContractProduct[], DefaultError>({
    queryKey: ['contact-products', contractId],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Contract/Products`,
        params: { contractId, itemCount: itemCount, page: pageParam },
      });

      const contracts = await responseHandler<
        IInfinitePageResponse<IContractProduct>
      >(response, {
        groupName: `useGetContactProducts - ${pageParam}`,
      });

      return infiniteHandler<IContractProduct>(contracts);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};
export const useGetContractTracks = (
  contractId: string,
  options?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    options?.page,
    options?.itemCount
  );

  return useInfiniteQuery<IContractTrack[], DefaultError>({
    queryKey: ['contact-tracks', contractId],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Contract/Tracks`,
        params: { contractId, itemCount: itemCount, page: pageParam },
      });

      const contracts = await responseHandler<
        IInfinitePageResponse<IContractTrack>
      >(response, {
        groupName: `useGetContactTracks - ${pageParam}`,
      });

      return infiniteHandler<IContractTrack>(contracts);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};
export const useGetContractVideos = (
  contractId: string,
  options?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    options?.page,
    options?.itemCount
  );

  return useInfiniteQuery<IContractVideo[], DefaultError>({
    queryKey: ['contact-videos', contractId],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Contract/Videos`,
        params: { contractId, itemCount: itemCount, page: pageParam },
      });

      const contracts = await responseHandler<
        IInfinitePageResponse<IContractVideo>
      >(response, {
        groupName: `useGetContactVideos - ${pageParam}`,
      });

      return infiniteHandler<IContractVideo>(contracts);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};
